import { useContext, useState } from "react";
import { Response } from "../../domain/app/Response";
import { AttendancePair } from "../../domain/attendance/Attendance";
import { AttendanceRepository } from "../../data/repository/AttendanceRepository";
import { AppTimestamp } from "../../domain/app/Timestamp";
import { UserAuthContext } from "../../ui/context/UserContext";
import { LocalErrorLogRepository } from "../../data/repository/LocalErrorLogRepository";
import { generateExcel } from "../../util/generateExcel";
import { request } from "http";

type FormModalValues = {
  [key: string]: any;
};

export function useByRequestAttendanceViewModel() {
  const [fetchState, setFetchState] = useState<Response<boolean> | null>(null);
  const [userAttendanceList, setUserAttendanceList] = useState<
    AttendancePair[] | undefined
  >();
  const [userAttendanceTimestamp, setUserAttendanceTimestamp] = useState<
    AppTimestamp | undefined
  >();
  const [refreshEvent, setRefreshEvent] = useState<boolean | null>(null);
  const { appUser } = useContext(UserAuthContext);

  //Filter
  const [filterEvent, setFilterEvent] = useState<boolean | null>(null);
  const [filterState, setFilterState] = useState<Response<boolean> | null>(
    null
  );
  const [filterResults, setFilterResults] = useState<AttendancePair[] | null>(
    null
  );

  // search
  const [searchEvent, setSearchEvent] = useState<boolean | null>(null);
  const [searchState, setSearchState] = useState<Response<boolean> | null>(
    null
  );
  const [searchResults, setSearchResults] = useState<AttendancePair[] | null>(
    null
  );

  // generate excel
  const [generateEvent, setGenerateEvent] = useState<boolean | null>(null);

  const [generateState, setGenerateState] = useState<Response<boolean> | null>(
    null
  );

  // modal alert
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [descriptionAlert, setDescriptionAlert] = useState("");

  async function fetchAttendance(
    timestamp: number,
    forceRefresh: boolean = false
  ) {
    if (fetchState?.isLoading()) return;
    setFetchState(Response.loading());
    try {
      console.log("appUser!.user!.clientUnity", appUser!.user!.clientUnity);

      const result = await AttendanceRepository.getGlobalList(
        timestamp,
        forceRefresh,
        appUser
      );
      console.log("results", result);
      setUserAttendanceList(result?.pairs);
      setUserAttendanceTimestamp(result?.timestamp);
      setFetchState(Response.success(true));
    } catch (e: any) {
      setFetchState(Response.failure(e));
    }
  }

  async function searchAttendance(values: FormModalValues) {
    setFetchState(Response.loading());
    try {
      const results = await AttendanceRepository.searchAttendance(
        values.filter,
        values.search,
        values.searchInAll === true ? null : values.dpInicio.getTime(),
        values.searchInAll === true ? null : values.dpFinal.getTime(),
        values.searchInAll
      );
      console.log("searchResult", results);
      setSearchResults(results || []);
      setFetchState(Response.success(true));
    } catch (e: any) {
      const label = `Error al buscar asistencia: '${values.search}'`;
      await LocalErrorLogRepository.registerError(label, e);
      setFetchState(Response.failure(new Error(label)));
    }
  }

  async function filterAttendance(
    values: FormModalValues,
    orderBy: string = "name",
    order: "asc" | "desc" = "asc"
  ) {
    setFetchState(Response.loading());
    try {
      const results = await AttendanceRepository.filterAttendance(
        values.filter,
        values.search,
        values.searchInAll === true ? null : values.dpInicio.getTime(),
        values.searchInAll === true ? null : values.dpFinal.getTime(),
        values.searchInAll
      );
      console.log("filterResult", results);
      if (results && results.length > 0) {
        results.sort((a: any, b: any) => {
          const valueA = a[orderBy]?.toLowerCase() || "";
          const valueB = b[orderBy]?.toLowerCase() || "";
          if (order === "asc") {
            return valueA.localeCompare(valueB);
          } else {
            return valueB.localeCompare(valueA);
          }
        });
      }
      setFilterResults(results || []);
      setFetchState(Response.success(true));
    } catch (e: any) {
      const label = `Error al filtrar asistencia: '${values.search}'`;
      await LocalErrorLogRepository.registerError(label, e);
      setFetchState(Response.failure(new Error(label)));
    }
  }

  async function toGenerateExcel() {
    setFetchState(Response.loading());
    try {
      console.log("to generate excel");
      console.log("search results", searchResults);
      if (searchResults?.length !== undefined && searchResults?.length > 0) {
        generateExcel(searchResults, "Attendance");
      } else {
        setIsAlertVisible(true);
        setDescriptionAlert(
          "Se necesita obtener resultados para generar Excel"
        );
      }

      setFetchState(Response.success(true));
    } catch (e: any) {
      const label = `Error al generar excel: ''`;
      await LocalErrorLogRepository.registerError(label, e);
      setFetchState(Response.failure(new Error(label)));
    }
  }

  function onFetchStateReceived() {
    setFetchState(null);
  }

  function requestFilterEvent() {
    setFilterEvent(true);
  }

  function requestRefreshEvent() {
    setRefreshEvent(true);
  }

  function onRefreshEventCompleted() {
    setRefreshEvent(null);
  }

  function requestSearchEvent() {
    setSearchEvent(true);
  }

  function onSearchEventCompleted() {
    setSearchEvent(null);
    setSearchResults(null);
  }

  function onFilterEventCompleted() {
    setFilterEvent(null);
    setFilterResults(null);
  }

  const [requestParams, setRequestParams] = useState<FormModalValues>({});
  function updateRequestParams(newParams: FormModalValues) {
    setRequestParams((prev) => ({ ...prev, ...newParams }));
  }

  return {
    //fetch
    fetchState,
    userAttendanceList,
    userAttendanceTimestamp,
    onFetchStateReceived,
    fetchAttendance,

    //refresh
    refreshEvent,
    requestRefreshEvent,
    onRefreshEventCompleted,

    //filter
    requestParams,
    updateRequestParams,
    filterEvent,
    filterState,
    filterResults,
    filterAttendance,
    onFilterEventCompleted,
    setFilterState,
    setFilterResults,
    requestFilterEvent,

    //search
    searchEvent,
    searchState,
    searchResults,
    requestSearchEvent,
    onSearchEventCompleted,
    searchAttendance,

    //generate
    toGenerateExcel,

    //alert
    isAlertVisible,
    setIsAlertVisible,
    descriptionAlert,
  };
}
